import React, { useState, useEffect, useRef } from 'react';
import { getLeaderboard } from '../../lib/adminApi';

function AdminLeaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await getLeaderboard();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchLeaderboard();
  }, []);

  // Format time from milliseconds to readable format
  const formatTime = (ms) => {
    if (ms == null || ms === 0) return '-';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Get rank badge style
  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-yellow-500 text-black';
      case 2:
        return 'bg-gray-300 text-black';
      case 3:
        return 'bg-orange-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  // Filter to only show users who have solved at least one question
  const solvedLeaderboard = leaderboard.filter((u) => u.questionsSolved > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Leaderboard</h1>
        <button
          onClick={fetchLeaderboard}
          className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700"
        >
          Refresh
        </button>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
        <table className="w-full">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Rank</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">User</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">Questions Solved</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">Total Time</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {solvedLeaderboard.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-400">
                  No one has solved any questions yet.
                </td>
              </tr>
            ) : (
              solvedLeaderboard.map((user, index) => (
                <React.Fragment key={user.userId}>
                  <tr className="hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${getRankStyle(
                          index + 1
                        )}`}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{user.email}</div>
                      <div className="text-gray-400 text-sm">ID: {user.userId}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg font-bold text-green-400">{user.questionsSolved}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-cyan-400 font-mono">
                        {formatTime(user.totalTimeTaken)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.questionDetails && user.questionDetails.length > 0 && (
                        <button
                          onClick={() =>
                            setExpandedUser(expandedUser === user.userId ? null : user.userId)
                          }
                          className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded text-sm hover:bg-blue-600/30"
                        >
                          {expandedUser === user.userId ? 'Hide' : 'Show'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Expanded details row */}
                  {expandedUser === user.userId && user.questionDetails && (
                    <tr className="bg-gray-900/50">
                      <td colSpan="5" className="px-4 py-4">
                        <div className="ml-12">
                          <h4 className="text-sm font-medium text-gray-300 mb-3">
                            Question Details
                          </h4>
                          <div className="grid gap-2">
                            {user.questionDetails.map((detail, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between bg-gray-800 rounded px-4 py-2"
                              >
                                <div className="flex items-center gap-4">
                                  <span className="text-gray-400">
                                    Question #{detail.questionId}
                                  </span>
                                  <span className="text-gray-500">•</span>
                                  <span className="text-gray-400">
                                    Attempts: <span className="text-white">{detail.attempts}</span>
                                  </span>
                                </div>
                                <div className="text-cyan-400 font-mono">
                                  {formatTime(detail.timeTaken)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      {solvedLeaderboard.length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm">Total Participants</div>
            <div className="text-2xl font-bold text-white">{leaderboard.length}</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm">Users with Solutions</div>
            <div className="text-2xl font-bold text-green-400">
              {solvedLeaderboard.length}
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-gray-400 text-sm">Top Solver</div>
            <div className="text-lg font-bold text-cyan-400 truncate">
              {solvedLeaderboard[0]?.email || '-'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLeaderboard;
